import { Button, Flex, Form } from "antd"
import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { oAuthInfoConfirmed, resendConfirmCode, verifyByPinCode } from "store/auth"
import { CustomForm } from "ui/Form"
import { CodeInput } from "ui/Input"
import { Text, Title } from "ui/Typography"
import { routes } from "router"
import useFormErrors from "hooks/useAntdFormErrors"
import { CustomInputError } from "ui/CustomInputError"
import { updateUser, getUser } from "store/user"
import ChangeContactModal from "../ChangeEmailPhoneNumber/ChangeContactModal"
import { toaster } from "components/common/Toast"
import { VERIFICATION_STATUS } from "layoutsV2/pro/PersonalInfoLayout/constants"
import styles from "./index.module.css"

const initialValue = ["", "", "", "", ""]

export const ConfirmPhone = () => {
  const { t } = useTranslation()
  const [initialCode, setInitialCode] = useState()
  const location = useLocation()
  const phone_number = location.state?.phone_number
  const oAuth = location.state?.oAuth
  const fromSignIn = location.state?.fromSignIn
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [timer, setTimer] = useState(0)
  const [updatedPhone, setUpdatedPhone] = useState("")
  const { loading: isResendloading } = useSelector((state) => state.auth.resendOtp)
  const { loading: isVerifyByOTPLoading } = useSelector((state) => state.auth.verifyByOTP)
  const [form] = Form.useForm()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleCloseModal = () => setIsModalOpen(false)

  const onFinish = ({ code }) => {
    const fullCode = code.join("")

    if (fullCode.length === 5) {
      dispatch(verifyByPinCode(fullCode)).then((response) => {
        if (!response) return

        // Check if we received auth tokens (automatic login)
        const hasAuthTokens = response.access_token && response.refresh_token

        if (hasAuthTokens) {
          // Tokens are already stored in localStorage by the auth reducer
          // Now fetch user data to check verification status and redirect appropriately
          dispatch(getUser()).then((userData) => {
            const { role, guide } = userData || {}

            if (oAuth) {
              dispatch(updateUser({ oauth_in_progress: false })).then(() => {
                dispatch({ type: oAuthInfoConfirmed })
              })
            }

            // Check if guide needs to complete setup wizard
            if (role === "guide_user" && guide?.verification_status === VERIFICATION_STATUS.IN_PROGRESS) {
              navigate(routes.proPersonalInfoRootPath(), { replace: true })
            } else if (role === "guide_user") {
              // Guide with completed setup goes to dashboard
              navigate(routes.guideDashboardPath(), { replace: true })
            } else {
              // Regular user goes to home
              navigate(routes.rootPath(), { replace: true })
            }
          })
        } else {
          // No tokens received, redirect to login page (old flow)
          if (oAuth === "true") {
            dispatch(updateUser({ oauth_in_progress: false })).then(() => {
              dispatch({ type: oAuthInfoConfirmed })
            })
          }
          navigate(routes.signInV2(), { replace: true })
        }
      })
    }
  }

  useEffect(() => {
    if (timer === 0) return

    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(interval)
  }, [timer])

  const handleResend = useCallback(
    (e) => {
      e?.preventDefault()
      dispatch(resendConfirmCode()).then((response) => {
        if (response?.success && response.message) {
          toaster.success(response.message)
        }
        setTimer(60)
        setInitialCode(initialValue)
      })
    },
    [dispatch]
  )

  const { errors } = useFormErrors(form)

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <Title level={2} className="justify-center mt-4xl mb-m">
        {t("verification.verify_phone_title")}
      </Title>
      {fromSignIn && (
        <Text className="justify-center mb-m text-center" type="warning">
          {t("verification.complete_signup_phone")}
        </Text>
      )}
      <Flex className="mb-4xl" vertical align="center">
        <Text className="justify-center pb-s">
          {t("enter_code.info_phone")}
          {updatedPhone || phone_number}
        </Text>
        {!fromSignIn && (
          <Button type="link" onClick={handleOpenModal}>
            {t("verification.change_phone")}
          </Button>
        )}

        {!!errors?.phone_number ? <CustomInputError errorKey={errors.phone_number} /> : null}
      </Flex>
      <CustomForm name="phone-confirmation" layout="vertical" onFinish={onFinish} form={form}>
        <CodeInput
          form={form}
          name="code"
          initialValue={initialCode}
          help={
            !!errors?.pin_code && (
              <CustomInputError
                errorKey={errors.pin_code}
                customHandler={
                  errors.pin_code.includes("user.attributes.pin_code.expired")
                    ? () => (
                        <Button
                          className="pl-0 pb-0 mb-0 mt-0 pt-0 fw-normal"
                          color="primary"
                          variant="link"
                          size="medium"
                          onClick={handleResend}
                        >
                          {t("enter_code.resend_code")}
                        </Button>
                      )
                    : null
                }
              />
            )
          }
        />
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            disabled={isResendloading || isVerifyByOTPLoading}
            loading={isResendloading || isVerifyByOTPLoading}
            className="mt-5xl"
          >
            {t("verification.verify")}
          </Button>
        </Form.Item>
      </CustomForm>
      <Flex justify="center">
        <div className={styles.resendContainer}>
          <Text>{t("enter_code.code_not_received")}</Text>
          {timer > 0 ? (
            <Text type="secondary">
              {t("enter_code.resend_in")}
              {timer} {t("enter_code.seconds")}
            </Text>
          ) : (
            <Button color="primary" variant="link" onClick={handleResend} disabled={isResendloading}>
              {t("enter_code.resend_code")}
            </Button>
          )}
        </div>
      </Flex>
      <ChangeContactModal open={isModalOpen} type="phone" onCancel={handleCloseModal} setUpdatedPhone={setUpdatedPhone} />
    </>
  )
}
